import { useLayoutEffect } from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';
// `Routes` is the Datadog-wrapped equivalent that emits a RUM view
// span on every route render. `Route`, `Navigate`, and the various
// hooks (`useParams`, `useLocation`, etc.) do not need wrapping and
// continue to come from `react-router-dom`.
import {
  AceptarInvitacion,
  ActivateAccount,
  NotFound,
  RecoverPassword,
} from '@/pages/auth';
import Ayuda from '@/pages/ayuda';
import { CausaDetalle, Causas } from '@/pages/causas';
import Configuracion from '@/pages/configuracion';
import Dashboard from '@/pages/dashboard';
import Oferta from '@/pages/oferta/Oferta.jsx';
import SolicitudDetailPreview from '@/pages/solicitudes/SolicitudDetailPreview.jsx';
import SolicitudesWorkspace from '@/pages/solicitudes/SolicitudesWorkspace.jsx';
import { Diligencias, VerDiligencia } from '@/pages/diligencias';
import Facturacion from '@/pages/facturacion';
import Pagos, { PaymentStatus, PublicPaymentResult } from '@/pages/pagos';
import { Perfil, PerfilLayout, PerfilPreview } from '@/pages/perfil';
import PlataformaPagos, {
  PlataformaConfiguracion,
  PlataformaDocumentos,
  PlataformaEventos,
  PlataformaWhatsApp,
} from '@/pages/plataforma';
import {
  ReceptorLayout,
  SolicitudDetail,
  Solicitudes,
  SolicitudesPreview,
} from '@/pages/solicitudes';
import { Routes } from '@datadog/browser-rum-react/react-router-v7';
import LoginRoute from './LoginRoute.jsx';
import MainLayout from './MainLayout.jsx';
import ProtectedRoute from './ProtectedRoute.jsx';
import { stopSessionReplayIfSensitive, syncSessionReplay } from './datadog.js';
import { RealLocationContext } from './realLocation.js';

export default function AppRoutes() {
  const location = useLocation();
  // Session replay stops while the URL carries a secret (see datadog.js). The
  // cut runs while rendering, not in an effect: the Datadog Routes below starts
  // the RUM view during its own render, and the replay has to be stopped
  // before that. It never resumes, so a render React abandons cannot restart
  // the recording with the page of the token still on screen.
  stopSessionReplayIfSensitive(location.search);
  // The resume waits for the commit. No dependency array on purpose: the
  // effect runs on every commit of AppRoutes, which only renders again when
  // the location changes. If a render towards a URL with a token is abandoned
  // and the history goes back to the entry already committed, nothing renders
  // and the replay stays stopped until the next navigation: replay coverage
  // lost, not privacy. An effect keyed on [location.search] would also miss a
  // commit that keeps the same search. syncSessionReplay only calls the SDK
  // start when the state changes, so the repeated call is free.
  useLayoutEffect(() => {
    syncSessionReplay(location.search);
  });
  // A trigger that opens the route-anchored create modal passes its own
  // location as `background`: the routes keep rendering that view while the
  // modal host (NuevaDiligenciaHost, in MainLayout) reads the real location
  // on top. Without a background (deep link) the real location renders.
  // The provider carries the real location INTO the routed tree: useLocation
  // in there returns `background || location` (React Router scopes
  // LocationContext to the location it matched against), so hosts read
  // RealLocationContext instead.
  const background = location.state?.background;
  return (
    <RealLocationContext.Provider value={location}>
      <Routes location={background || location}>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/activar-cuenta" element={<ActivateAccount />} />
        <Route path="/oferta" element={<Oferta />} />
        <Route path="/recuperar-contrasena" element={<RecoverPassword />} />
        {/* Public: an invited user has no session yet. */}
        <Route path="/aceptar-invitacion" element={<AceptarInvitacion />} />
        {/* Toku Checkout Session return (REC-1503). Public on purpose: the
          canal pagador has no Recepthor cookie. `/payment/status` stays
          inside ProtectedRoute for the logged-in estudio cobro. */}
        <Route
          path="/pago/recibido"
          element={<PublicPaymentResult outcome="recibido" />}
        />
        <Route
          path="/pago/no-completado"
          element={<PublicPaymentResult outcome="no-completado" />}
        />
        <Route
          element={
            <ProtectedRoute
              component={MainLayout}
              allowedCapabilities={['enterprise_law_firm', 'solo_law_firm']}
            />
          }
        >
          <Route path="/" element={<Dashboard />} />
          <Route path="/inicio" element={<Navigate to="/" replace />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/causas" element={<Causas />} />
          {/* The standalone form is gone (REC-1792): /causas opens the same
            modal in place, so a saved link to the old page still lands there
            with the modal open instead of 404ing. */}
          <Route
            path="/causas/nueva"
            element={<Navigate to="/causas?nueva=1" replace />}
          />
          <Route path="/diligencias" element={<Diligencias />} />
          {/* Old bookmarks to the standalone path keep resolving. */}
          <Route
            path="/diligencias-v2"
            element={<Navigate to="/diligencias" replace />}
          />
          {/* Creation lives in the route-anchored modal (NuevaDiligenciaHost,
            mounted in MainLayout over the current view). This route is only
            the deep-link fallback: with no background view to keep, the list
            renders behind the modal. */}
          <Route path="/diligencias/nueva" element={<Diligencias />} />
          <Route path="/diligencias/:id" element={<VerDiligencia />} />
          {/* Notificaciones salió del menú (sección retirada). Las actuaciones
            del PJUD siguen visibles dentro de cada causa; los deep links
            antiguos aterrizan en Causas. */}
          <Route
            path="/notificaciones"
            element={<Navigate to="/causas" replace />}
          />
          <Route
            path="/notificaciones/:id"
            element={<Navigate to="/causas" replace />}
          />
          <Route
            path="/sistema"
            element={<Navigate to="/sistema/cuenta" replace />}
          />
          <Route path="/sistema/cuenta" element={<Configuracion />} />
          <Route path="/sistema/organizacion" element={<Configuracion />} />
          <Route
            path="/configuracion"
            element={<Navigate to="/sistema/cuenta" replace />}
          />
          {/* Facturación tab lives in the URL so it is linkable and survives a
            refresh (REC-1108). Bare /facturacion and the old settings path both
            land on the default tab. */}
          <Route
            path="/facturacion"
            element={<Navigate to="/facturacion/tarifa" replace />}
          />
          <Route path="/facturacion/:tab" element={<Facturacion />} />
          <Route
            path="/configuracion/facturacion"
            element={<Navigate to="/facturacion/tarifa" replace />}
          />
          <Route path="/causas/:caseId" element={<CausaDetalle />} />
          {/* Deprecated: cotizar folded into the Nueva diligencia modal. */}
          <Route
            path="/causas/:caseId/cotizar"
            element={<Navigate to="/diligencias/nueva" replace />}
          />
          <Route path="/pagos" element={<Pagos />} />
          <Route
            path="/cotizaciones"
            element={<Navigate to="/pagos" replace />}
          />
          <Route
            path="/cotizaciones/:cotId/pagar"
            element={<Navigate to="/pagos" replace />}
          />
          <Route path="/ayuda" element={<Ayuda />} />
          {/* Retorno de Toku → confirma y aterriza en /pagos con toast. Vive en
            MainLayout (necesita el ToastProvider). */}
          <Route path="/payment/status" element={<PaymentStatus />} />
        </Route>
        {process.env.NODE_ENV !== 'production' ? (
          <Route element={<ReceptorLayout preview />}>
            <Route element={<SolicitudesWorkspace />}>
              <Route
                path="/solicitudes/preview"
                element={<SolicitudesPreview />}
              />
              <Route
                path="/solicitudes/preview/:quoteId"
                element={<SolicitudDetailPreview />}
              />
            </Route>
          </Route>
        ) : null}
        {process.env.NODE_ENV !== 'production' ? (
          <Route element={<PerfilLayout preview />}>
            <Route path="/perfil/preview" element={<PerfilPreview />} />
          </Route>
        ) : null}
        <Route
          element={
            <ProtectedRoute
              component={ReceptorLayout}
              allowedCapabilities={['receptor']}
              loginTo="/login?portal=receptor"
            />
          }
        >
          <Route element={<SolicitudesWorkspace />}>
            <Route path="/solicitudes" element={<Solicitudes />} />
            <Route path="/solicitudes/:quoteId" element={<SolicitudDetail />} />
          </Route>
        </Route>
        <Route
          element={
            <ProtectedRoute
              component={PerfilLayout}
              allowedCapabilities={['receptor']}
              loginTo="/login?portal=receptor"
            />
          }
        >
          <Route path="/perfil" element={<Perfil />} />
        </Route>
        {/* Plataforma (backoffice): reuses MainLayout with its own nav. Two
          views — the payout queue for receptores, and what the provider sent
          alongside the money that matched no deal. */}
        <Route
          element={
            <ProtectedRoute
              component={MainLayout}
              allowedCapabilities={['backoffice']}
            />
          }
        >
          <Route
            path="/plataforma"
            element={<Navigate to="/plataforma/whatsapp" replace />}
          />
          <Route
            path="/plataforma/cola"
            element={<Navigate to="/plataforma/whatsapp" replace />}
          />
          <Route path="/plataforma/whatsapp" element={<PlataformaWhatsApp />} />
          <Route path="/plataforma/pagos" element={<PlataformaPagos />} />
          <Route path="/plataforma/eventos" element={<PlataformaEventos />} />
          <Route
            path="/plataforma/configuracion"
            element={<PlataformaConfiguracion />}
          />
          <Route
            path="/plataforma/documentos"
            element={<PlataformaDocumentos />}
          />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </RealLocationContext.Provider>
  );
}
